// Týdenní graf poměru přidání do košíku — víc čar (např. Celkem / PC / Mobil)
// a svislé značky poznámek. Sdílí ho detail produktu i souhrn e-shopu.

import { useState } from 'react';

export type WeekPoint = {
  week: string;
  start: string;
  views: number;
  adds: number;
  rate: number | null;
};

export type ChartLine = {
  label: string;
  color: string;
  points: WeekPoint[];
  /** Tenčí přerušovaná čára (srovnávací, např. celý e-shop). */
  muted?: boolean;
};

export type ChartMarker = { id: number; date: string; color: string };

export const fmtPct = (n: number | null) =>
  n == null ? '–' : `${n.toFixed(1).replace('.', ',')} %`;

export const fmtDen = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}. ${y}`;
};

export const mondayOf = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

const W = 700;
const H = 260;
const PAD = { l: 44, r: 12, t: 12, b: 28 };

export function WeeklyRateChart({
  lines,
  markers = [],
}: {
  lines: ChartLine[];
  markers?: ChartMarker[];
}) {
  const [hover, setHover] = useState<number | null>(null);
  const weeks = lines[0]?.points ?? [];
  const all = lines.flatMap((l) => l.points.map((p) => p.rate ?? 0));
  const maxRate = Math.max(5, ...all) * 1.1;
  const stepX = weeks.length > 1 ? (W - PAD.l - PAD.r) / (weeks.length - 1) : 0;
  const x = (i: number) => PAD.l + i * stepX;
  const y = (v: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / maxRate);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxRate);
  const labelEvery = Math.max(1, Math.ceil(weeks.length / 8));

  // Čára jen přes týdny s daty — týden bez zobrazení ji přeruší.
  const pathOf = (pts: WeekPoint[]) => {
    let d = '';
    pts.forEach((p, i) => {
      if (p.rate == null) return;
      const prev = pts[i - 1];
      d += `${i === 0 || prev?.rate == null ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.rate).toFixed(1)} `;
    });
    return d;
  };

  const hovered = hover != null ? weeks[hover] : null;

  return (
    <div>
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-text-secondary">
        {lines.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 16 4" className="h-1 w-4" aria-hidden="true">
              <line
                x1="0"
                x2="16"
                y1="2"
                y2="2"
                stroke={l.color}
                strokeWidth="4"
                strokeDasharray={l.muted ? '4 3' : undefined}
              />
            </svg>
            {l.label}
          </span>
        ))}
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto block"
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const sx = ((e.clientX - rect.left) / rect.width) * W;
            const i = Math.round((sx - PAD.l) / (stepX || 1));
            setHover(i >= 0 && i < weeks.length ? i : null);
          }}
        >
          <title>Týdenní poměr přidání do košíku</title>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={y(t)}
                y2={y(t)}
                stroke="currentColor"
                strokeOpacity="0.1"
              />
              <text
                x={PAD.l - 6}
                y={y(t) + 3}
                textAnchor="end"
                className="text-[10px] fill-current text-text-muted"
              >
                {t.toFixed(0)} %
              </text>
            </g>
          ))}
          {markers.map((m) => {
            const i = weeks.findIndex((p) => p.start === mondayOf(m.date));
            if (i < 0) return null;
            return (
              <line
                key={m.id}
                x1={x(i)}
                x2={x(i)}
                y1={PAD.t}
                y2={H - PAD.b}
                stroke={m.color}
                strokeWidth="2"
                strokeDasharray="4 3"
              />
            );
          })}
          {hover != null && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD.t}
              y2={H - PAD.b}
              stroke="currentColor"
              strokeOpacity="0.2"
            />
          )}
          {lines.map((l) => (
            <g key={l.label}>
              <path
                d={pathOf(l.points)}
                fill="none"
                stroke={l.color}
                strokeWidth={l.muted ? 1.5 : 2}
                strokeDasharray={l.muted ? '5 4' : undefined}
              />
              {!l.muted &&
                l.points.map((p, i) =>
                  p.rate == null ? null : (
                    <circle
                      key={p.week}
                      cx={x(i)}
                      cy={y(p.rate)}
                      r={hover === i ? 4.5 : 2.5}
                      fill={l.color}
                    />
                  ),
                )}
            </g>
          ))}
          {weeks.map((p, i) =>
            i % labelEvery === 0 ? (
              <text
                key={`l${p.week}`}
                x={x(i)}
                y={H - 8}
                textAnchor="middle"
                className="text-[10px] fill-current text-text-muted"
              >
                {Number(p.start.slice(8))}.{Number(p.start.slice(5, 7))}.
              </text>
            ) : null,
          )}
        </svg>
        {hovered && hover != null && (
          <div className="absolute top-2 right-2 bg-text text-white text-xs rounded px-2.5 py-1.5 pointer-events-none">
            <div className="mb-0.5">týden od {fmtDen(hovered.start)}</div>
            {lines.map((l) => {
              const p = l.points[hover];
              return (
                <div key={l.label}>
                  {l.label}: <strong>{fmtPct(p?.rate ?? null)}</strong>
                  <span className="opacity-70">
                    {' '}
                    ({p?.adds ?? 0} / {p?.views ?? 0})
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
